import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  cn,
} from "@nexus/ui";
import type { PreflightMarketKind } from "@nexus/contracts";
import { groupMarketsByKind } from "../../lib/market-order";
import { ManageMarketsDialog } from "../ManageMarketsDialog/ManageMarketsDialog";
import { MarketIcon } from "../MarketIcon/MarketIcon";
import type { PreflightMarketWithCount } from "../../types/preflight.types";

/** `null` is the all-markets scope, which is the hub default (R7). */
export type MarketScope = string | null;

/**
 * `scope` offers all-markets alongside each market (hub default, R7). `single`
 * drops it, because a report belongs to exactly one market (R12) — the create
 * form uses this mode and `null` there means "not chosen yet".
 */
export type MarketSelectMode = "scope" | "single";

export interface MarketSelectProps {
  markets: PreflightMarketWithCount[];
  value: MarketScope;
  onChange: (scope: MarketScope) => void;
  disabled?: boolean;
  mode?: MarketSelectMode;
  /** Trigger height. The hub header pairs it with a primary CTA, so it uses `default`. */
  size?: "sm" | "default";
  /** Extra trigger classes — the create form stretches it across its grid column. */
  className?: string;
}

/**
 * Market selector for the Pre Flight hub and the create form: every market with
 * its report count, all-markets in scope mode, and a footer action onto the manage
 * panel in both modes (R7, R12, R14). Selection state stays with the caller so the
 * whole hub reads from one source (R8).
 */
export function MarketSelect({
  markets,
  value,
  onChange,
  disabled = false,
  mode = "scope",
  size = "sm",
  className,
}: MarketSelectProps) {
  const { t } = useTranslation();
  const [isManageOpen, setIsManageOpen] = useState(false);

  const isSingleSelect = mode === "single";
  const allMarketsLabel = t("preflight.markets.allMarkets", "All markets");
  const selected = markets.find((market) => market.id === value);
  const scopeLabel =
    selected?.name ??
    (isSingleSelect
      ? t("preflight.markets.selectPlaceholder", "Select a market")
      : allMarketsLabel);
  const triggerAria = isSingleSelect
    ? t("preflight.markets.selectAria", "Market: {{market}}", {
        market:
          selected?.name ??
          t("preflight.markets.noneSelected", "none selected"),
      })
    : t("preflight.markets.scopeAria", "Market scope: {{scope}}", {
        scope: scopeLabel,
      });

  const marketGroups = useMemo(() => groupMarketsByKind(markets), [markets]);

  function handleMarketRemoved(marketId: string) {
    if (marketId === value) onChange(null);
  }

  const isAllMarkets = !isSingleSelect && value === null;
  const totalReportCount = markets.reduce(
    (total, market) => total + market.reportCount,
    0,
  );

  return (
    <>
      {/*
        modal={false}: when nested in a Radix Dialog, a modal menu sets
        body pointer-events:none via DismissableLayer. Closing that menu
        while the dialog also dismisses can leave the body unclickable
        (AD-2098). Non-modal menu avoids the second layer.
      */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size={size}
            iconEnd="chevron-down"
            disabled={disabled}
            className={cn(
              "min-w-48 max-w-xs cursor-pointer justify-between",
              className,
            )}
            aria-label={triggerAria}
          >
            <span className="inline-flex min-w-0 items-center gap-2">
              {selected ? (
                <MarketIcon
                  kind={selected.kind}
                  code={selected.code}
                  name={selected.name}
                />
              ) : !isSingleSelect ? (
                <ScopeDot selected square={isAllMarkets} />
              ) : null}
              <span
                className={cn(
                  "truncate",
                  isSingleSelect && !selected && "text-muted-foreground",
                )}
              >
                {scopeLabel}
              </span>
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          {!isSingleSelect && (
            <>
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  onChange(null);
                }}
                className="flex cursor-pointer items-center justify-between gap-2.5 font-medium"
              >
                <span className="inline-flex min-w-0 items-center gap-2.5">
                  <ScopeDot selected={isAllMarkets} square />
                  <span className="min-w-0 truncate">{allMarketsLabel}</span>
                </span>
                <ReportCount value={totalReportCount} />
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {marketGroups.length > 0 ? (
            marketGroups.map((group, groupIndex) => (
              <DropdownMenuGroup key={group.kind}>
                {groupIndex > 0 ? <DropdownMenuSeparator /> : null}
                <KindHeading kind={group.kind} />
                {group.markets.map((market) => (
                  <DropdownMenuItem
                    key={market.id}
                    onClick={(event) => {
                      event.stopPropagation();
                      onChange(market.id);
                    }}
                    className="flex cursor-pointer items-center justify-between gap-2.5"
                  >
                    <span className="inline-flex min-w-0 items-center gap-2.5">
                      <ScopeDot selected={market.id === value} />
                      <MarketIcon
                        kind={market.kind}
                        code={market.code}
                        name={market.name}
                      />
                      <span className="min-w-0 truncate">{market.name}</span>
                    </span>
                    <ReportCount value={market.reportCount} />
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            ))
          ) : (
            <div
              role="status"
              className="text-muted-foreground px-2 py-3 text-center text-xs"
            >
              {t(
                "preflight.markets.selectEmpty",
                "No markets yet — add one below.",
              )}
            </div>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={(event) => {
              event.stopPropagation();
              setIsManageOpen(true);
            }}
            className="text-primary cursor-pointer font-medium"
          >
            <Icon name="plus" size="sm" color="inherit" />
            <span>
              {t("preflight.markets.addOrManage", "Add or manage markets")}
            </span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ManageMarketsDialog
        open={isManageOpen}
        onOpenChange={setIsManageOpen}
        markets={markets}
        onMarketRemoved={handleMarketRemoved}
      />
    </>
  );
}

/** Global is a single row — no section heading (same as the manage catalogue). */
function KindHeading({ kind }: { kind: PreflightMarketKind }) {
  const { t } = useTranslation();

  if (kind === "GLOBAL") return null;

  const label =
    kind === "CONTINENT"
      ? t("preflight.markets.groupContinents", "Continents")
      : t("preflight.markets.groupCountries", "Countries");

  return <DropdownMenuLabel>{label}</DropdownMenuLabel>;
}

/** Selection marker; the square shape distinguishes the all-markets roll-up from a market. */
function ScopeDot({
  selected,
  square = false,
}: {
  selected: boolean;
  square?: boolean;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "size-2 shrink-0",
        square ? "rounded-xs" : "rounded-full",
        selected ? "bg-primary" : "bg-muted-foreground/35",
      )}
    />
  );
}

function ReportCount({ value }: { value: number }) {
  const { t } = useTranslation();
  return (
    <span
      className="text-muted-foreground shrink-0 text-xs tabular-nums"
      aria-label={t("preflight.markets.reportCount", {
        count: value,
        defaultValue_one: "{{count}} report",
        defaultValue_other: "{{count}} reports",
      })}
    >
      {value}
    </span>
  );
}
